import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { nanoid } from 'nanoid';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import type { Task } from '@shared/schema';
import { recordPulseRead } from '@/lib/pulse-user-tags';

export interface TaskDraft {
  id: string;
  text: string;
  status: string;
  sequenceId?: string;
  assigneeId?: string;
  dueDate?: Date;
  messageId?: number;
}

type ViewMode = 'tasks' | 'chat' | 'attachments';

interface PulseWorkbenchState {
  isOpen: boolean;
  activePulseId: number | null;
  viewMode: ViewMode;
  drafts: TaskDraft[];
  isSubmitting: boolean;
}

interface PulseWorkbenchContextValue extends PulseWorkbenchState {
  sessionId: string;
  openWorkbench: (pulseId: number, initialView?: ViewMode) => void;
  closeWorkbench: () => void;
  setViewMode: (mode: ViewMode) => void;
  addDraft: (text: string, options?: Partial<Omit<TaskDraft, 'id' | 'text'>>) => void;
  removeDraft: (draftId: string) => void;
  updateDraft: (draftId: string, updates: Partial<TaskDraft>) => void;
  clearDrafts: () => void;
  commitDrafts: () => Promise<{ created: Task[]; failed: { draftId: string; error: string }[] }>;
}

const PulseWorkbenchContext = createContext<PulseWorkbenchContextValue | null>(null);

export function usePulseWorkbenchContext() {
  const context = useContext(PulseWorkbenchContext);
  if (!context) {
    throw new Error('usePulseWorkbenchContext must be used within PulseWorkbenchProvider');
  }
  return context;
}

export function usePulseWorkbenchContextSafe() {
  return useContext(PulseWorkbenchContext);
}

interface PulseWorkbenchProviderProps {
  children: ReactNode;
}

// Module-scoped session ID map to handle multiple tabs correctly
const SESSION_ID_REGISTRY = new Map<string, string>();

export function PulseWorkbenchProvider({ children }: PulseWorkbenchProviderProps) {
  const queryClient = useQueryClient();
  
  const sessionIdRef = useRef<string>(nanoid());
  
  // Register session ID for this provider instance
  // Use a unique key per provider to handle multiple tabs
  const providerKeyRef = useRef<string>(nanoid(8));
  SESSION_ID_REGISTRY.set(providerKeyRef.current, sessionIdRef.current);
  
  // Expose session ID checker function instead of raw value
  if (typeof window !== 'undefined') {
    (window as any).__PULSE_WORKBENCH_SESSION_ID__ = sessionIdRef.current;
    (window as any).__PULSE_WORKBENCH_CHECK_SESSION__ = (incomingId: string) => {
      // Check if the incoming session ID matches ANY registered session
      const values = Array.from(SESSION_ID_REGISTRY.values());
      return values.some(registeredId => registeredId === incomingId);
    };
  }
  
  const [state, setState] = useState<PulseWorkbenchState>({
    isOpen: false,
    activePulseId: null,
    viewMode: 'tasks',
    drafts: [],
    isSubmitting: false,
  });

  const openWorkbench = useCallback((pulseId: number, initialView: ViewMode = 'tasks') => {
    void recordPulseRead(pulseId);
    setState((prev) => ({
      ...prev,
      isOpen: true,
      activePulseId: pulseId,
      viewMode: initialView,
      drafts: [],
    }));
  }, []);

  const closeWorkbench = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
      activePulseId: null,
      drafts: [],
      isSubmitting: false,
    }));
  }, []);

  const setViewMode = useCallback((mode: ViewMode) => {
    setState((prev) => ({ ...prev, viewMode: mode }));
  }, []);

  const addDraft = useCallback((text: string, options: Partial<Omit<TaskDraft, 'id' | 'text'>> = {}) => {
    const newDraft: TaskDraft = {
      id: nanoid(),
      text,
      status: options.status || 'to do',
      sequenceId: options.sequenceId,
      assigneeId: options.assigneeId,
      dueDate: options.dueDate,
      messageId: options.messageId,
    };
    setState((prev) => ({
      ...prev,
      drafts: [...prev.drafts, newDraft],
    }));
  }, []);

  const removeDraft = useCallback((draftId: string) => {
    setState((prev) => ({
      ...prev,
      drafts: prev.drafts.filter((d) => d.id !== draftId),
    }));
  }, []);

  const updateDraft = useCallback((draftId: string, updates: Partial<TaskDraft>) => {
    setState((prev) => ({
      ...prev,
      drafts: prev.drafts.map((d) =>
        d.id === draftId ? { ...d, ...updates } : d
      ),
    }));
  }, []);

  const clearDrafts = useCallback(() => {
    setState((prev) => ({ ...prev, drafts: [] }));
  }, []);

  const commitDrafts = useCallback(async (): Promise<{ created: Task[]; failed: { draftId: string; error: string }[] }> => {
    if (!state.activePulseId || state.drafts.length === 0) {
      return { created: [], failed: [] };
    }

    setState((prev) => ({ ...prev, isSubmitting: true }));

    const validationErrors: { draftId: string; error: string }[] = [];
    const validDrafts: TaskDraft[] = [];

    for (const draft of state.drafts) {
      const trimmedText = draft.text.trim();
      if (!trimmedText) {
        validationErrors.push({ draftId: draft.id, error: 'Task description cannot be empty' });
        continue;
      }
      if (trimmedText.length > 1000) {
        validationErrors.push({ draftId: draft.id, error: 'Task description exceeds 1000 characters' });
        continue;
      }
      validDrafts.push({ ...draft, text: trimmedText });
    }

    if (validDrafts.length === 0) {
      setState((prev) => ({ ...prev, isSubmitting: false }));
      return { created: [], failed: validationErrors };
    }

    try {
      const tasksToCreate = validDrafts.map((draft) => ({
        draftId: draft.id,
        taskDescription: draft.text,
        status: draft.status || 'to do',
        dueDate: draft.dueDate?.toISOString() || null,
        assignedArtist: draft.assigneeId || null,
        messageId: draft.messageId || null,
        sequenceId: draft.sequenceId || null,
      }));

      const response = await apiRequest(
        'POST',
        `/api/pulses/${state.activePulseId}/tasks/batch`,
        {
          tasks: tasksToCreate,
          sessionId: sessionIdRef.current,
        }
      );

      const result = await response.json();

      // Handle validation error response (server returns 200 with validationError flag)
      if (result.validationError) {
        setState((prev) => ({ ...prev, isSubmitting: false }));
        return { 
          created: [], 
          failed: [...validationErrors, ...(result.failed || [])] 
        };
      }

      const allFailed = [
        ...validationErrors,
        ...(result.failed || []).map((f: any) => ({
          draftId: f.draftId,
          error: f.error || 'Unknown server error',
        })),
      ];

      if (result.created && result.created.length > 0) {
        // CLEAN SLATE: Keep local workbench update for immediate user feedback
        queryClient.setQueryData(
          ['pulse-workbench', state.activePulseId],
          (old: any) => {
            if (!old) return old;
            const existingIds = new Set(old.tasks?.map((t: any) => t.id) || []);
            const newTasks = result.created.filter((t: Task) => !existingIds.has(t.id));
            return {
              ...old,
              tasks: [...old.tasks, ...newTasks],
            };
          }
        );
        
        // CLEAN SLATE: Simple invalidation for tree - placeholderData prevents flicker
        // Server is source of truth for task counts and tree structure
        queryClient.invalidateQueries({ 
          queryKey: ['/api/tree/snapshot'],
          exact: false 
        });
      }

      const failedDraftIds = new Set(allFailed.map((f) => f.draftId));
      setState((prev) => ({
        ...prev,
        drafts: prev.drafts.filter((d) => failedDraftIds.has(d.id)),
        isSubmitting: false,
      }));

      return { created: result.created || [], failed: allFailed };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      const failedAll = state.drafts.map((d) => ({
        draftId: d.id,
        error: errorMessage,
      }));
      setState((prev) => ({ ...prev, isSubmitting: false }));
      return { created: [], failed: failedAll };
    }
  }, [state.activePulseId, state.drafts, queryClient]);

  const value: PulseWorkbenchContextValue = {
    ...state,
    sessionId: sessionIdRef.current,
    openWorkbench,
    closeWorkbench,
    setViewMode,
    addDraft,
    removeDraft,
    updateDraft,
    clearDrafts,
    commitDrafts,
  };

  return (
    <PulseWorkbenchContext.Provider value={value}>
      {children}
    </PulseWorkbenchContext.Provider>
  );
}
